import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  playlistRefKey,
  useStore,
  type PlaylistTuneRef,
  type TunesBrowseScope,
} from '@/state/store';
import { usePlaylists, usePlaylistsAvailable } from '@/data/playlistAccess';
import { canEditPlaylist } from '@/storage/playlistLinks';
import { parseIRealSongChunk } from '@/music/ireal/parse';
import { isAbcScheme, parseTuneIndex } from '@/music/tuneIndex';
import { withAbcBookName } from '@/music/abc/book';
import {
  BUNDLED_PLAYLISTS,
  fetchBundledPlaylist,
  playlistDisplayName,
} from '@/data/bundledPlaylists';
import { BUNDLED_SCORES } from '@/data/bundledScores';
import { openbookLibrary, type OpenbookLibraryEntry } from '@/data/openbookLibrary';
import { realbookLibrary, type RealbookLibraryEntry } from '@/data/realbookLibrary';
import { useLibraryTunes } from '@/data/libraryTunes';
import { SONG_PRESETS } from '@/data/presets';
import { collectTuneSources, tuneVersionLabel, type TuneSourceEntry } from '@/data/tuneSources';
import { PlaylistScoreUpdate } from './PlaylistScoreUpdate';

interface ImportPanelProps {
  onClose: () => void;
  onImported: (title: string) => void;
  /** The home page's search opens the import form first. */
  importFirst?: boolean;
}

/**
 * Rows render in pages of this size so a 1460-song list paints fast; scrolling
 * near the bottom of the picker loads the next page, so the whole list is
 * reachable without searching.
 */
const PAGE_SIZE = 200;

/** What the browser screen is currently scoped to. Every scope reads from the
 * same shared tune-source aggregation the ⌘K palette searches. The store's
 * `tunesBrowseScope` remembers this across open/close ('home' = scope null). */
type BrowseScope = Exclude<TunesBrowseScope, { type: 'home' }>;

/**
 * The Tunes library: browse every source the app can load — native Score
 * Studio scores, bundled iReal playlists, OpenBook, saved tunes and your playlists — or
 * import an iReal Pro link/file. The home search spans all sources at once.
 * A single pasted song loads immediately; a playlist opens the browser.
 */
export function ImportPanel({ onClose, onImported, importFirst = false }: ImportPanelProps) {
  const loadChart = useStore((s) => s.loadChart);
  const loadTuneFromChunk = useStore((s) => s.loadTuneFromChunk);
  const setPlaylist = useStore((s) => s.setPlaylist);
  const clearPlaylist = useStore((s) => s.clearPlaylist);
  const playlist = useStore((s) => s.playlist);
  const savedCharts = useStore((s) => s.savedCharts);
  const userPlaylists = usePlaylists();
  // Signed out there are no playlists: no section, no Add to menus.
  const playlistsOn = usePlaylistsAvailable();
  const playlistLinks = useStore((s) => s.playlistLinks);
  // A playlist shared with you view only is not yours to add to.
  const addTargets = useMemo(
    () => userPlaylists.filter((p) => canEditPlaylist(playlistLinks, p.id)),
    [userPlaylists, playlistLinks],
  );
  const createPlaylist = useStore((s) => s.createPlaylist);
  const renamePlaylist = useStore((s) => s.renamePlaylist);
  const deletePlaylist = useStore((s) => s.deletePlaylist);
  const addToPlaylist = useStore((s) => s.addToPlaylist);
  const removeFromPlaylist = useStore((s) => s.removeFromPlaylist);

  const setTunesBrowseScope = useStore((s) => s.setTunesBrowseScope);

  const hasPlaylist = !!playlist && playlist.songs.length > 1;
  // The first open of a session lands HOME — every source on one screen —
  // rather than inside the loaded iReal list (user-found Sep 2026: "the tunes
  // dialogue seems to open on the 1460"; a 1,460-row list is the wrong first
  // screen for "find me a tune"). After that, reopen where the panel was last
  // left; a remembered scope whose backing content is gone (cleared playlist,
  // deleted custom list) falls back home.
  const [scope, rawSetScope] = useState<BrowseScope | null>(() => {
    if (importFirst) return null;
    const remembered = useStore.getState().tunesBrowseScope;
    if (!remembered) return null;
    if (remembered.type === 'home') return null;
    if (remembered.type === 'loaded' && !hasPlaylist) return null;
    if (
      remembered.type === 'custom' &&
      !useStore.getState().userPlaylists.some((p) => p.id === remembered.id)
    ) {
      return null;
    }
    return remembered;
  });
  const setScope = (next: BrowseScope | null) => {
    rawSetScope(next);
    setTunesBrowseScope(next ?? { type: 'home' });
  };
  const [openbook, setOpenbook] = useState<OpenbookLibraryEntry[]>([]);
  const [realbook, setRealbook] = useState<RealbookLibraryEntry[]>([]);
  const library = useLibraryTunes();
  const leadSheets = openbook.length + realbook.length;
  const [query, setQuery] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedTuneKey, setLoadedTuneKey] = useState<string | null>(null);
  const [addMenuFor, setAddMenuFor] = useState<string | null>(null);
  // Metadata filters, reset with the scope.
  const [melodyOnly, setMelodyOnly] = useState(false);
  const [styleFilter, setStyleFilter] = useState('');
  const [keyFilter, setKeyFilter] = useState('');
  // Inline playlist editing (no window.prompt): the add-menu's new-playlist
  // name, the home section's create form, and per-row rename / armed delete.
  const [menuNaming, setMenuNaming] = useState(false);
  /** A playlist this row is already in, armed for a deliberate second copy.
   * Cleared by the row's Add-to toggle as well as by the item's blur: Safari
   * never focuses a tapped button, so that blur never fires there, and an arm
   * that outlived its menu let one tap in the next menu add a duplicate. */
  const [confirmAddFor, setConfirmAddFor] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Transient per-row confirmation after an Add-to ("✓ Added to X" — or the
  // honest "Already in X" when the store's dedupe made it a no-op).
  const [addFeedback, setAddFeedback] = useState<{ rowKey: string; text: string } | null>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => () => clearTimeout(feedbackTimer.current ?? undefined), []);

  // Search can read a persisted import without changing the active store.
  // Opening its management panel is the point to restore it, so Browse and
  // Clear work after a fresh page load too. Never replace a live import.
  useEffect(() => {
    const store = useStore.getState();
    if (!store.playlist) store.rehydratePlaylist();
  }, []);

  const showAddFeedback = (rowKey: string, text: string) => {
    clearTimeout(feedbackTimer.current ?? undefined);
    setAddFeedback({ rowKey, text });
    feedbackTimer.current = setTimeout(() => setAddFeedback(null), 2000);
  };

  // The openbook manifest is one small fetch, cached for the session — load it
  // as soon as the panel opens so its tunes join the browser and home search.
  useEffect(() => {
    let cancelled = false;
    void openbookLibrary().then((entries) => {
      if (!cancelled) setOpenbook(entries);
    });
    void realbookLibrary().then((entries) => {
      if (!cancelled) setRealbook(entries);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const allEntries = useMemo(
    () =>
      collectTuneSources({
        presets: SONG_PRESETS,
        savedCharts,
        openbook,
        realbook,
        library,
        userPlaylists,
        playlist,
      }),
    [savedCharts, openbook, realbook, library, userPlaylists, playlist],
  );

  const activeCustom =
    scope?.type === 'custom' ? (userPlaylists.find((p) => p.id === scope.id) ?? null) : null;
  const browsing = scope != null && (scope.type !== 'custom' || activeCustom != null);

  const rows: TuneSourceEntry[] = useMemo(() => {
    if (!scope) return [];
    switch (scope.type) {
      case 'all':
        return allEntries;
      case 'loaded':
        return allEntries.filter((e) => e.source === 'tune' || e.source === 'abc');
      case 'native':
        return allEntries.filter((e) => e.source === 'native');
      case 'lead':
        return allEntries.filter((e) => e.source === 'openbook' || e.source === 'realbook');
      case 'library':
        return allEntries.filter((e) => e.source === 'library');
      case 'saved':
        return allEntries.filter((e) => e.source === 'saved');
      case 'custom':
        return allEntries.filter((e) => e.source === 'playlist' && e.playlistId === scope.id);
    }
  }, [scope, allEntries]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!q ||
          r.title.toLowerCase().includes(q) ||
          (r.composer ?? '').toLowerCase().includes(q) ||
          (r.context ?? '').toLowerCase().includes(q)) &&
        (!melodyOnly || r.melody) &&
        (!styleFilter || r.style === styleFilter) &&
        (!keyFilter || r.musicKey === keyFilter),
    );
  }, [rows, query, melodyOnly, styleFilter, keyFilter]);

  // Filter options come from the current scope's rows, so the selects only
  // ever offer values that exist here.
  const styleOptions = useMemo(
    () => [...new Set(rows.map((r) => r.style).filter((s): s is string => !!s))].sort(),
    [rows],
  );
  const keyOptions = useMemo(
    () => [...new Set(rows.map((r) => r.musicKey).filter((k): k is string => !!k))].sort(),
    [rows],
  );
  const anyMelody = useMemo(() => rows.some((r) => r.melody), [rows]);

  // Lazy list growth: start with one page and add another whenever the picker
  // scrolls near its end. A new search / scope starts back at one page.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, scope, playlist]);

  const growOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      setVisibleCount((count) => (count < matches.length ? count + PAGE_SIZE : count));
    }
  };

  const goBrowse = (next: BrowseScope) => {
    setQuery('');
    setLoadedTuneKey(null);
    setError(null);
    setMelodyOnly(false);
    setStyleFilter('');
    setKeyFilter('');
    setAddMenuFor(null);
    setMenuNaming(false);
    setScope(next);
  };

  const openBundled = async (id: string) => {
    const entry = BUNDLED_PLAYLISTS.find((p) => p.id === id);
    if (!entry) return;
    setBusy(true);
    setError(null);
    try {
      const src = await fetchBundledPlaylist(entry);
      const res = setPlaylist(src);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setWarning(
        res.persisted ? null : 'Loaded for this session only — too large to save across reloads.',
      );
      goBrowse({ type: 'loaded' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that playlist.');
    } finally {
      setBusy(false);
    }
  };

  const loadEntry = (entry: TuneSourceEntry) => {
    setError(null);
    void entry
      .load()
      .then((title) => {
        setLoadedTuneKey(entry.key);
        onImported(title);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load that tune.');
      });
  };

  /** An iReal Pro link or an ABC tune or tunebook, pasted or from a file. */
  const doImport = (raw: string, fileName?: string) => {
    let index;
    try {
      index = parseTuneIndex(raw);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that link.');
      return;
    }
    const abc = isAbcScheme(index.scheme);
    if (index.songs.length === 0) {
      setError(abc ? 'No tunes found in that ABC file.' : 'No songs found in the iReal Pro link.');
      return;
    }
    setError(null);
    if (index.songs.length === 1) {
      if (abc) {
        const title = loadTuneFromChunk(index.scheme, index.songs[0].chunk);
        if (!title) return setError('Could not read that tune.');
        onImported(title);
        return;
      }
      const chart = parseIRealSongChunk(index.scheme, index.songs[0].chunk);
      if (!chart) return setError('Could not read that song.');
      loadChart(chart);
      onImported(chart.title);
      return;
    }
    // A tunebook that does not name itself takes its file's name.
    const res = setPlaylist(abc ? withAbcBookName(raw, fileName) : raw);
    if (!res.ok) return setError(res.error);
    setWarning(
      res.persisted ? null : 'Loaded for this session only — too large to save across reloads.',
    );
    goBrowse({ type: 'loaded' });
  };

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => doImport(String(reader.result ?? ''), file.name);
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsText(file);
  };

  /**
   * Add a browser row to a playlist.
   *
   * Already in that list? Arm a confirmation rather than refusing (this used
   * to say "Already in X" and do nothing) or silently duplicating. A set can
   * legitimately hold a tune twice, so the answer is to ask — and the header's
   * Add-to menu asks the same way, because two entry points to one action
   * behaving differently is what made this confusing.
   */
  const addTuneTo = (rowKey: string, playlistId: string, ref: PlaylistTuneRef) => {
    const list = userPlaylists.find((p) => p.id === playlistId);
    const already =
      !!list && list.entries.some((e) => playlistRefKey(e.ref) === playlistRefKey(ref));
    if (already && confirmAddFor !== playlistId) {
      setConfirmAddFor(playlistId);
      return;
    }
    addToPlaylist(playlistId, ref);
    setAddMenuFor(null);
    setMenuNaming(false);
    setConfirmAddFor(null);
    showAddFeedback(rowKey, `✓ Added to ${list?.name ?? 'playlist'}`);
  };

  /** Create a playlist from the inline name draft; optionally seed it. */
  const createFromDraft = (ref?: PlaylistTuneRef) => {
    const name = nameDraft.trim();
    if (!name) return null;
    const id = createPlaylist(name);
    if (ref) {
      addToPlaylist(id, ref);
      if (addMenuFor) showAddFeedback(addMenuFor, `✓ Added to ${name}`);
    }
    setNameDraft('');
    setMenuNaming(false);
    setCreating(false);
    setAddMenuFor(null);
    return id;
  };

  // ---------- Browser ----------
  if (browsing && scope) {
    const shown = matches.slice(0, visibleCount);
    const title =
      scope.type === 'custom'
        ? activeCustom!.name
        : scope.type === 'native'
          ? 'Scores'
          : scope.type === 'lead'
            ? 'Lead sheets'
            : scope.type === 'library'
              ? 'Library'
              : scope.type === 'saved'
                ? 'Saved tunes'
                : scope.type === 'all'
                  ? 'All tunes'
                  : (playlistDisplayName(playlist?.name) ?? 'Playlist');
    const showSourceBadge = scope.type === 'all';
    return (
      <section className="import-panel" data-testid="import-panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>
            {title} — {matches.length}/{rows.length} tunes
          </h3>
          <button type="button" className="close-circle" onClick={onClose} aria-label="Close tunes">
            <X size={15} aria-hidden="true" />
          </button>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn btn-sm"
            data-testid="playlist-back"
            onClick={() => {
              setQuery('');
              setScope(null);
            }}
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Tunes
          </button>
          <input
            className="playlist-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title or composer…"
            aria-label="Search tunes"
            data-testid="playlist-search"
            autoFocus
          />
        </div>
        {(anyMelody || styleOptions.length > 1 || keyOptions.length > 1) && (
          <div className="tune-filters" role="group" aria-label="Filter tunes">
            {anyMelody && (
              <button
                type="button"
                className={`filter-chip${melodyOnly ? ' active' : ''}`}
                aria-pressed={melodyOnly}
                data-testid="filter-melody"
                onClick={() => setMelodyOnly((v) => !v)}
              >
                {'♪'} Melody
              </button>
            )}
            {styleOptions.length > 1 && (
              <select
                className="filter-select"
                value={styleFilter}
                onChange={(e) => setStyleFilter(e.target.value)}
                aria-label="Filter by style"
                data-testid="filter-style"
              >
                <option value="">Any style</option>
                {styleOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
            {keyOptions.length > 1 && (
              <select
                className="filter-select"
                value={keyFilter}
                onChange={(e) => setKeyFilter(e.target.value)}
                aria-label="Filter by key"
                data-testid="filter-key"
              >
                <option value="">Any key</option>
                {keyOptions.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div className="playlist-picker" data-testid="playlist-picker" onScroll={growOnScroll}>
          {shown.map((entry) => {
            const ref = entry.ref;
            return (
              <div className="playlist-row" key={entry.key}>
                <button
                  className={`btn btn-sm playlist-song${entry.key === loadedTuneKey ? ' is-active' : ''}`}
                  onClick={() => loadEntry(entry)}
                >
                  <span className="playlist-song-title">
                    {entry.title}
                    {entry.composer ? <span className="muted"> · {entry.composer}</span> : null}
                    {/* A library tune credits whoever wrote it up. */}
                    {entry.source === 'library' && entry.context ? (
                      <span className="muted"> · {entry.context}</span>
                    ) : null}
                  </span>
                  <span className="tune-badges">
                    {entry.resolvable === 'missing' && (
                      <span className="set-queue-gone">unavailable</span>
                    )}
                    {entry.melody && (
                      <span
                        className="tune-melody"
                        title="Has a written melody"
                        aria-label="has melody"
                      >
                        {'♪'}
                      </span>
                    )}
                    {entry.lyrics && (
                      <span className="tune-lyrics" title="Has lyrics" aria-label="has lyrics">
                        {'abc'}
                      </span>
                    )}
                    {showSourceBadge && (
                      <span className="tune-source">{tuneVersionLabel(entry.source)}</span>
                    )}
                  </span>
                </button>
                {scope.type === 'custom' ? (
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Remove from this playlist"
                    onClick={() =>
                      entry.playlistEntryId && removeFromPlaylist(scope.id, entry.playlistEntryId)
                    }
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                ) : addFeedback?.rowKey === entry.key ? (
                  <span className="add-feedback" role="status" data-testid="playlist-added">
                    {addFeedback.text}
                  </span>
                ) : ref && playlistsOn ? (
                  <div className="add-wrap">
                    <button
                      className="btn btn-sm btn-ghost"
                      data-testid="playlist-add"
                      aria-label="Add to playlist"
                      onClick={() => {
                        setMenuNaming(false);
                        setNameDraft('');
                        setConfirmAddFor(null);
                        setAddMenuFor(addMenuFor === entry.key ? null : entry.key);
                      }}
                    >
                      <Plus size={14} aria-hidden="true" />
                      Add to
                      <ChevronDown size={13} aria-hidden="true" />
                    </button>
                    {addMenuFor === entry.key && (
                      <div className="add-menu" role="menu">
                        {addTargets.map((p) => {
                          const already = p.entries.some(
                            (e) => playlistRefKey(e.ref) === playlistRefKey(ref),
                          );
                          const arming = confirmAddFor === p.id;
                          return (
                            <button
                              key={p.id}
                              className={`add-menu-item${already ? ' in-list' : ''}${
                                arming ? ' is-arming' : ''
                              }`}
                              onClick={() => addTuneTo(entry.key, p.id, ref)}
                              onBlur={() => arming && setConfirmAddFor(null)}
                            >
                              {p.name}
                              {arming ? (
                                <span className="add-menu-again">Already in — add again?</span>
                              ) : (
                                already && (
                                  <span
                                    className="add-menu-check"
                                    aria-label="already in this playlist"
                                  >
                                    ✓
                                  </span>
                                )
                              )}
                            </button>
                          );
                        })}
                        {menuNaming ? (
                          <input
                            className="text-input add-menu-name"
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') createFromDraft(ref);
                              if (e.key === 'Escape') {
                                setMenuNaming(false);
                                setNameDraft('');
                              }
                            }}
                            placeholder="New playlist name…"
                            aria-label="New playlist name"
                            data-testid="add-menu-name"
                            autoFocus
                          />
                        ) : (
                          <button
                            className="add-menu-item add-menu-new"
                            onClick={() => {
                              setNameDraft('');
                              setMenuNaming(true);
                            }}
                          >
                            <Plus size={14} aria-hidden="true" />
                            New playlist…
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
                <PlaylistScoreUpdate row={entry} onUpdated={() => setError(null)} />
              </div>
            );
          })}
          {matches.length === 0 && (
            <p className="muted" style={{ fontSize: 13 }}>
              No matches.
            </p>
          )}
          {matches.length > shown.length && (
            <p className="playlist-more" data-testid="playlist-more">
              +{matches.length - shown.length} more — scroll to load them.
            </p>
          )}
        </div>
        {warning && (
          <p className="import-error" role="status">
            {warning}
          </p>
        )}
        {error && (
          <p className="import-error" role="alert">
            {error}
          </p>
        )}
        {scope.type === 'loaded' && hasPlaylist && (
          <div className="row" style={{ marginTop: 8 }}>
            <button
              className="btn btn-sm btn-ghost"
              data-testid="playlist-clear"
              onClick={() => {
                clearPlaylist();
                setScope(null);
              }}
            >
              Clear loaded playlist
            </button>
          </div>
        )}
      </section>
    );
  }

  const importForm = (
    <div className="tunes-section">
      <span className="label">Import an iReal Pro link or ABC</span>
      <textarea
        className="import-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="irealb://… or X:1"
        rows={2}
        aria-label="iReal Pro link or ABC"
        data-testid="import-text"
      />
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        <button
          className="btn btn-sm btn-primary"
          data-testid="import-submit"
          onClick={() => doImport(text)}
          disabled={!text.trim()}
        >
          Import
        </button>
        <button className="btn btn-sm" onClick={() => fileRef.current?.click()}>
          Upload file…
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".html,.htm,.txt,.abc,.abct,text/html,text/plain,text/vnd.abc"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );

  // ---------- Home ----------
  return (
    <section className="import-panel" data-testid="import-panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>{importFirst ? 'Import tunes' : 'Tunes'}</h3>
        <button type="button" className="close-circle" onClick={onClose} aria-label="Close tunes">
          <X size={15} aria-hidden="true" />
        </button>
      </div>

      {importFirst && importForm}

      <input
        className="playlist-search"
        type="text"
        value=""
        onChange={(e) => {
          const value = e.target.value;
          if (!value) return;
          // Same reset as any scope change — stale filters from a previous
          // browse must not silently empty the all-sources search.
          goBrowse({ type: 'all' });
          setQuery(value);
        }}
        placeholder="Search every source — tunes, melodies, saved…"
        aria-label="Search all tunes"
        data-testid="tunes-search-all"
      />

      <div className="tunes-section">
        <span className="label">Built-in</span>
        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {BUNDLED_PLAYLISTS.map((p) => (
            <button
              key={p.id}
              className="btn btn-sm btn-primary"
              data-testid={`bundled-${p.id}`}
              disabled={busy}
              onClick={() => openBundled(p.id)}
            >
              {busy ? 'Loading…' : p.name}
            </button>
          ))}
          <button
            className="btn btn-sm btn-primary"
            data-testid="source-native"
            onClick={() => goBrowse({ type: 'native' })}
          >
            {'♫'} Scores {BUNDLED_SCORES.length}
          </button>
          <button
            className="btn btn-sm btn-primary"
            data-testid="source-lead"
            onClick={() => goBrowse({ type: 'lead' })}
          >
            {'♪'} Lead sheets{leadSheets ? ` ${leadSheets}` : ''}
          </button>
          {hasPlaylist && (
            <button
              className="btn btn-sm"
              data-testid="source-loaded"
              onClick={() => goBrowse({ type: 'loaded' })}
            >
              Browse loaded ({playlist!.songs.length})
            </button>
          )}
        </div>
      </div>

      {library.length > 0 && (
        <div className="tunes-section">
          <span className="label">Library</span>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <button
              className="btn btn-sm"
              data-testid="source-library"
              onClick={() => goBrowse({ type: 'library' })}
            >
              Browse library ({library.length})
            </button>
          </div>
        </div>
      )}

      {savedCharts.length > 0 && (
        <div className="tunes-section">
          <span className="label">Saved tunes</span>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <button
              className="btn btn-sm"
              data-testid="source-saved"
              onClick={() => goBrowse({ type: 'saved' })}
            >
              Browse saved ({savedCharts.length})
            </button>
          </div>
        </div>
      )}

      {playlistsOn && (
        <div className="tunes-section">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="label">My playlists</span>
            {!creating && (
              <button
                className="btn btn-sm"
                data-testid="playlist-new"
                onClick={() => {
                  setNameDraft('');
                  setCreating(true);
                }}
              >
                <Plus size={14} aria-hidden="true" />
                New
              </button>
            )}
          </div>
          {creating && (
            <div className="row" style={{ gap: 8 }}>
              <input
                className="text-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const id = createFromDraft();
                    if (id) goBrowse({ type: 'custom', id });
                  }
                  if (e.key === 'Escape') {
                    setCreating(false);
                    setNameDraft('');
                  }
                }}
                placeholder="Playlist name…"
                aria-label="New playlist name"
                data-testid="playlist-name-input"
                autoFocus
              />
              <button
                className="btn btn-sm btn-primary"
                data-testid="playlist-create"
                disabled={!nameDraft.trim()}
                onClick={() => {
                  const id = createFromDraft();
                  if (id) goBrowse({ type: 'custom', id });
                }}
              >
                Create
              </button>
              <button
                className="btn btn-sm btn-ghost"
                aria-label="Cancel new playlist"
                onClick={() => {
                  setCreating(false);
                  setNameDraft('');
                }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          )}
          {userPlaylists.length === 0 && !creating ? (
            <p className="muted" style={{ fontSize: 13 }}>
              No playlists yet — open a tune and use “+ Add to” to start one.
            </p>
          ) : (
            <div className="my-playlists">
              {userPlaylists.map((p) =>
                renamingId === p.id ? (
                  <div className="playlist-row" key={p.id}>
                    <input
                      className="text-input"
                      defaultValue={p.name}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          renamePlaylist(p.id, (e.target as HTMLInputElement).value);
                          setRenamingId(null);
                        }
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      onBlur={(e) => {
                        renamePlaylist(p.id, e.target.value);
                        setRenamingId(null);
                      }}
                      aria-label={`Rename ${p.name}`}
                      data-testid="playlist-rename-input"
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="playlist-row" key={p.id}>
                    <button
                      className="btn btn-sm playlist-song"
                      onClick={() => goBrowse({ type: 'custom', id: p.id })}
                    >
                      {p.name} <span className="muted">· {p.entries.length}</span>
                    </button>
                    {canEditPlaylist(playlistLinks, p.id) && (
                      <button
                        className="btn btn-sm btn-ghost"
                        aria-label={`Rename ${p.name}`}
                        onClick={() => {
                          setConfirmDeleteId(null);
                          setRenamingId(p.id);
                        }}
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                    )}
                    {confirmDeleteId === p.id ? (
                      <button
                        className="btn btn-sm btn-danger"
                        aria-label={`Confirm delete ${p.name}`}
                        onBlur={() => setConfirmDeleteId(null)}
                        onClick={() => {
                          deletePlaylist(p.id);
                          setConfirmDeleteId(null);
                        }}
                      >
                        Delete?
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-ghost"
                        aria-label={`Delete ${p.name}`}
                        onClick={() => setConfirmDeleteId(p.id)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      )}

      {!importFirst && importForm}

      {error && (
        <p className="import-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
